/**
 * Singleton store backing the embed's surface state — open/close, mode,
 * conversationId, hostApp, hostContext. Imperative hooks
 * (`useAgentOpen`, `useAgentSubmit`, …) read/write through this so a
 * CTA from anywhere in the host app can pop the topbar Sheet and submit.
 *
 * `conversationId` + `mode` persist to localStorage so reloads continue
 * the same chat. Per user feedback: it's one ongoing conversation across
 * the whole embed; no per-host pinning. Power-user history at
 * agent.ordo.com/chats.
 *
 * The store does NOT own the conversation engine — that lives in
 * `useConversation`. The store is the surface-level "is the dialog open?
 * which mode? which conversation are we currently bound to?" state.
 */

import { create } from 'zustand'

import type { AgentMode } from '../types'

const LS_CONVERSATION = 'agent-embed:conversationId'
const LS_CONVERSATION_AT = 'agent-embed:conversationLastActiveAt'
const LS_MODE = 'agent-embed:mode'

// Idle TTL for the persisted conversation. After this much wall-clock
// time without activity (no setConversationId call), the next time the
// embed boots it drops the saved id and starts fresh. Picks 6h so a
// half-day later "I'm back at my desk" auto-clears, but a tab refresh
// or a coffee break does not. Manual clear is always available via
// the chat header's "New chat" button.
const CONVERSATION_IDLE_TTL_MS = 6 * 60 * 60 * 1000

function readLs(key: string): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeLs(key: string, value: string | null): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, value)
    }
  } catch {
    /* private mode / quota — silent fail */
  }
}

/**
 * Read the persisted conversation id, but only if its last-active
 * timestamp is still within the idle window. Stale entries get evicted
 * here so a new browser session (or a long pause) auto-starts a fresh
 * chat without surfacing yesterday's thread.
 */
function readPersistedConversationId(): string | null {
  const id = readLs(LS_CONVERSATION)
  if (!id) {
    return null
  }
  const rawAt = readLs(LS_CONVERSATION_AT)
  const lastAt = rawAt ? Number.parseInt(rawAt, 10) : NaN
  if (!Number.isFinite(lastAt) || Date.now() - lastAt > CONVERSATION_IDLE_TTL_MS) {
    writeLs(LS_CONVERSATION, null)
    writeLs(LS_CONVERSATION_AT, null)
    return null
  }
  return id
}

function readPersistedMode(): AgentMode {
  // Plan-37: Ask mode is gone. Persisted value can only be 'search' or
  // 'agent' now. Stale 'ask' values from older builds collapse to 'agent'.
  const raw = readLs(LS_MODE)
  if (raw === 'search' || raw === 'agent') {
    return raw
  }
  return 'agent'
}

interface AgentStoreState {
  open: boolean
  mode: AgentMode
  conversationId: string | null
  hostApp: string | null
  hostContext: Record<string, unknown> | null
  pendingSubmit: { text: string; mode?: AgentMode } | null
  /** The unified input's current text. Drives Search-mode live-filter
   *  and is sent to Ask/Agent when the user hits Enter. Cleared after
   *  send. NOT persisted across reloads — ephemeral by design. */
  composerText: string
  /** When true, force the empty palette (chips/rows/recent) even
   *  though `conversationId` is set + `messages.length > 0`. Used by
   *  the chat-header's "back to search" button so the user can see
   *  their launchpad without losing the active chat. Cleared by
   *  submitting and by clicking the resume-chat row; the header's
   *  "New chat" button SETS it (fresh thread → launchpad). NOT
   *  persisted —
   *  every fresh open lands in chat mode if a conversation exists. */
  paletteOverride: boolean

  setOpen: (open: boolean) => void
  toggle: () => void
  setMode: (mode: AgentMode) => void
  setConversationId: (id: string | null) => void
  setHostApp: (hostApp: string) => void
  setHostContext: (ctx: Record<string, unknown> | null) => void
  setComposerText: (text: string) => void
  setPaletteOverride: (on: boolean) => void
  /** Imperative submit: queue a message + open the dialog. The active
   *  surface picks up `pendingSubmit` on mount and calls send(). */
  queueSubmit: (text: string, opts?: { mode?: AgentMode }) => void
  consumePendingSubmit: () => { text: string; mode?: AgentMode } | null
}

export const useAgentStore = create<AgentStoreState>((set, get) => ({
  open: false,
  mode: readPersistedMode(),
  conversationId: readPersistedConversationId(),
  hostApp: null,
  hostContext: null,
  pendingSubmit: null,
  composerText: '',
  paletteOverride: false,

  setOpen: (open) =>
    set(
      open
        ? // Every open lands in the palette state — the launchpad is the
          // default. If a chat exists, the Resume row appears at the top
          // for one-keystroke resume. Submitting (or picking a chip)
          // flips paletteOverride off and moves into chat mode.
          { open: true, paletteOverride: true }
        : // Closing also resets to false so the next open is fresh.
          { open: false, paletteOverride: false },
    ),
  toggle: () => set((s) => ({ open: !s.open })),
  setMode: (mode) => {
    writeLs(LS_MODE, mode)
    set({ mode })
  },
  setConversationId: (id) => {
    writeLs(LS_CONVERSATION, id)
    // Touch the last-active timestamp so the idle TTL is measured from
    // "most recent activity" rather than "first set". On null, drop it
    // too — there's nothing to revive.
    writeLs(LS_CONVERSATION_AT, id ? String(Date.now()) : null)
    set({ conversationId: id })
  },
  setHostApp: (hostApp) => set({ hostApp }),
  setHostContext: (ctx) => set({ hostContext: ctx }),
  setComposerText: (text) => set({ composerText: text }),
  setPaletteOverride: (on) => set({ paletteOverride: on }),
  queueSubmit: (text, opts) =>
    set({
      open: true,
      mode: opts?.mode ?? get().mode,
      pendingSubmit: { text, ...(opts?.mode ? { mode: opts.mode } : {}) },
    }),
  consumePendingSubmit: () => {
    const pending = get().pendingSubmit
    if (pending) {
      set({ pendingSubmit: null })
    }
    return pending
  },
}))
